/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { AttributeValue, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore,
    IEmailDelivery,
    IInvoicePaymentSummary
} from '@msdyn365-commerce-modules/checkout';
import { ICheckoutProps } from '../definition-extensions/checkout.ext.props.autogenerated';
import { Modal, IModalProps, ModalHeader, ModalBody, ModalFooter, Button } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { RichTextComponent } from '@msdyn365-commerce/core';
import {
    GetProductAttributesCartlinesInput,
    getProductAttributeCartlineAction
} from '../../../actions/get-product-attributes-cartlines.action';
import { PaymentStateShareInput } from '../../../actions/payment-state-share.action';

export interface SimpleProductExtended extends SimpleProduct {
    Attributes?: AttributeValue[];
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;
let data: ProductPrice[] = [];
if (MsDyn365.isBrowser && typeof Storage !== 'undefined' && typeof localStorage !== 'undefined') {
    let productPrices = JSON.parse(localStorage.getItem('productPrices') || '[]'); //getting data from localStorage
    data = productPrices;
}

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => {
    const lineItemClone = item && React.cloneElement(item as JSX.Element, { productPrices: data }); // receiving data from localstorage
    return <Node {...LineItem}>{lineItemClone}</Node>;
};

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => {
    const customSubTotal = React.cloneElement(lines?.subtotal as React.ReactElement, { freeText: 0 });
    const customCustomerAccountLabel =
        lines?.customerAccount &&
        React.cloneElement(lines?.customerAccount as React.ReactElement, {
            label: 'Credit Applied to Account'
        });
    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {customSubTotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {lines.tax}
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {customCustomerAccountLabel}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<{}>> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef
    } = props;
    const [isRepairable, setIsRepairableItem] = React.useState<boolean>(false);
    React.useEffect(() => {
        const getProductAttributes = async () => {
            const products: SimpleProductExtended[] | undefined = await props.data.products;
            if (products) {
                for (let product of products) {
                    const getAttributeValueInput = new GetProductAttributesCartlinesInput(Number(product.RecordId));
                    try {
                        const productSpecificationData = await getProductAttributeCartlineAction(
                            getAttributeValueInput,
                            props.context.actionContext
                        );

                        product.Attributes = productSpecificationData.productAttributes;
                    } catch (e) {}
                }
                for (let product of products) {
                    if (product && product.Attributes) {
                        const attributeValue = product.Attributes.find((attribute: AttributeValue) => attribute.Name === 'Repairable')
                            ?.BooleanValue;
                        if (attributeValue) {
                            setIsRepairableItem(attributeValue);
                            break;
                        }
                    }
                }
            }
        };

        getProductAttributes();
    }, []);
    const {
        config: { labelTermsAndCondtions, descriptionTerms, labelRepairableTerms, descriptionRepairableTerms },
        resources: { viewTermsLink, modalCloseButtonLabel }
    } = props;

    // Initialized state for terms and conditions checkbox
    const [isTermsChecked, setIsTermsChecked] = React.useState(false);
    const [isRepairTermsChecked, setIsRepairTermsChecked] = React.useState(false);

    //Cloned the place order button to control the place order button
    const placeOrderButtonEl = placeOrderButton as JSX.Element;

    React.useEffect(() => {
        if (props.data.checkout && props.data.checkout.result) {
            if (props.data.checkout.result.checkoutCart.hasInvoiceLine) {
                setIsTermsChecked(true);
            }
        }
    }, [props.data.checkout && props.data.checkout.result && props.data.checkout.result.checkoutCart.hasInvoiceLine]);

    if (props.data.checkout && props.data.checkout.result) {
        if (props.data.checkout.result.checkoutCart.hasInvoiceLine) {
            props.context.actionContext.update(new PaymentStateShareInput(), {
                isCreditCardSelected: true,
                isAccountPaymentSelected: false
            });
        }
    }

    // Handler for terms and conditions checkbox change
    const handleTermsAndConditionsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsTermsChecked(event.target.checked);
    };

    // Handler for repairable terms and conditions checkbox change
    const handleRepairableTermsAndConditionsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsRepairTermsChecked(event.target.checked);
    };

    //Cloned this to control the actual place order button
    const placeOrderButtonClone =
        placeOrderButton &&
        React.cloneElement(placeOrderButton as JSX.Element, {
            canPlaceOrder: isRepairable
                ? placeOrderButtonEl.props.canPlaceOrder && isTermsChecked && isRepairTermsChecked
                : placeOrderButtonEl.props.canPlaceOrder && isTermsChecked
        });

    // Modal Open/Close State
    const [showModalTerms, setShowModalTerms] = React.useState(false);
    const [showModalRepairable, setShowModalRepairable] = React.useState(false);

    const toggleModalTerms = () => {
        // call this function to toggle the state of modal
        setShowModalTerms(!showModalTerms);
    };

    const toggleModalRepair = () => {
        setShowModalRepairable(!showModalRepairable);
    };
    //Imported Modal Props from MSDYN Core
    const modalPropsTerms: IModalProps = {
        applicationNode: MsDyn365.isBrowser ? document.body : '',
        isOpen: showModalTerms,
        autoFocus: true,
        size: 'sm',
        keyboard: true,
        backdrop: true,
        zIndex: 1000,
        fade: true,
        toggle: toggleModalTerms
    };

    const modalPropsRepair: IModalProps = {
        applicationNode: MsDyn365.isBrowser ? document.body : '',
        isOpen: showModalRepairable,
        autoFocus: true,
        size: 'sm',
        keyboard: true,
        backdrop: true,
        zIndex: 1000,
        fade: true,
        toggle: toggleModalRepair
    };

    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                {props.data.checkout &&
                                props.data.checkout.result &&
                                props.data.checkout.result.checkoutCart.hasInvoiceLine ? (
                                    <></>
                                ) : (
                                    <Node className='ms-checkout-terms-and-conditions'>
                                        <label className='ms-checkout-terms-and-conditions__container'>
                                            <input
                                                type='checkbox'
                                                checked={isTermsChecked}
                                                onChange={handleTermsAndConditionsCheckboxChange}
                                                className='ms-checkout-terms-and-conditions__input-checkbox'
                                            />
                                            <span className='ms-checkout-terms-and-conditions__text'>
                                                {labelTermsAndCondtions}
                                                <a
                                                    href='javascript:void(0)'
                                                    onClick={toggleModalTerms}
                                                    className='ms-checkout__terms-and-conditions__link'
                                                >
                                                    {viewTermsLink}
                                                </a>
                                            </span>
                                        </label>
                                        <Modal {...modalPropsTerms}>
                                            <ModalHeader
                                                closeAriaLabel='Close'
                                                closeButtonLabel='Close'
                                                toggle={toggleModalTerms}
                                            ></ModalHeader>
                                            <ModalBody>
                                                <RichTextComponent text={descriptionTerms} />
                                            </ModalBody>
                                            <ModalFooter>
                                                <Button onClick={toggleModalTerms} className='btn-primary'>
                                                    {modalCloseButtonLabel}
                                                </Button>
                                            </ModalFooter>
                                        </Modal>
                                    </Node>
                                )}
                                {isRepairable && (
                                    <Node className='ms-checkout-terms-and-conditions'>
                                        <label className='ms-checkout-terms-and-conditions__container'>
                                            <input
                                                type='checkbox'
                                                checked={isRepairTermsChecked}
                                                onChange={handleRepairableTermsAndConditionsCheckboxChange}
                                                className='ms-checkout-terms-and-conditions__input-checkbox'
                                            />
                                            <span className='ms-checkout-terms-and-conditions__text'>
                                                {labelRepairableTerms}
                                                <a
                                                    href='javascript:void(0)'
                                                    onClick={toggleModalRepair}
                                                    className='ms-checkout__terms-and-conditions__link'
                                                >
                                                    {viewTermsLink}
                                                </a>
                                            </span>
                                        </label>
                                        <Modal {...modalPropsRepair}>
                                            <ModalHeader
                                                closeAriaLabel='Close'
                                                closeButtonLabel='Close'
                                                toggle={toggleModalRepair}
                                            ></ModalHeader>
                                            <ModalBody>
                                                <RichTextComponent text={descriptionRepairableTerms} />
                                            </ModalBody>
                                            <ModalFooter>
                                                <Button onClick={toggleModalRepair} className='btn-primary'>
                                                    {modalCloseButtonLabel}
                                                </Button>
                                            </ModalFooter>
                                        </Modal>
                                    </Node>
                                )}
                                <Node {...mainControlProps}>
                                    {placeOrderButtonClone}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && <OrderSummaryComponent {...orderSummary} />
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButtonClone}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItems} />}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButtonClone}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
